import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import Container from '@mui/material/Container';
import { fetchStatuses } from '../../constants/fetchStatuses';
import { useSearchParamsState, usePermissions } from '../../hooks';
import { useAppDispatch } from '../../redux/hooks';
import { cleanAlpacas } from '../../redux/modules/alpaca/alpaca.actions';
import {
  fetchKycByUserId,
  fetchKycGenderOptions,
  fetchKycStatusOptions,
  fetchKycTypeOptions,
  fetchUser,
  fetchUserStatusOptions,
  fetchUserWorldCheckScreen,
  resetEditUser,
  resetUserApiStatuses,
} from '../../redux/modules/user/user.actions';
import { userKycLoadingSelector, userLoadingSelector, userSelector } from '../../redux/modules/user/user.selectors';
import { fetchUserCustodianData } from '../../redux/modules/userCustodianData/userCustodianData.actions';
import { fetchUserGroups } from '../../redux/modules/userGroup/userGroup.actions';
import * as permissions from '../../utils/permissions';
import { AttachmentsTab } from './components/Attachments/AttachmentsTab';
import { BankInfoTab } from './BankInfoTab';
import { KycTab } from './components/Kyc/KycTab';
import { OwnedSharesTab } from './components/OwnedShares/OwnedSharesTab';
import { UserHistoryTab } from './UserHistoryTab';
import { UserSignAgreementsTab } from './UserSignAgreementsTab';
import { UserSurveyResultsTab } from './UserSurveyResultsTab';
import { UserTab } from './UserTab';
import { AlpacaTab } from './components/Alpaca/AlpacaTab';
import { WorldCheckTab } from './WorldCheckTab';
import { FeesTab } from './components/Fees/FeesTab';
import { Ledger } from '../../components/RefactoredLedger';
import { useFetchLedgerQuery } from '../../redux/features/ledger/refactoredLedger.apiSlice';
import TimelineTab from './TimelineTab';
import UserNotesTab from './components/Notes/UserNotesTab';
import SubscriptionTab from './components/Subscription/SubscriptionTab';
import { UserPageHeader } from './UserPageHeader';
import { SideTabsLayout, SideTabDescriptor } from '../../components/SideTabs';
import { DEFAULT_TAB_VALUE, mapTabParamToValue, mapTabValueToTabParam, userTabSections } from './userTabs';

const KYC_TAB_VALUE = mapTabParamToValue.kyc;

export function User() {
  const { id } = useParams();
  const dispatch = useAppDispatch();
  const [selectedTab, setSelectedTab] = useSearchParamsState('tab', 'default');

  const canReadKyc = usePermissions({ items: [permissions.Kyc.read] });
  const { editUser, updateKycStatus, createStatus, updateStatus } = useSelector(userSelector);
  const isUserLoading = useSelector(userLoadingSelector);
  const isKycLoading = useSelector(userKycLoadingSelector);

  const isLoading =
    isUserLoading ||
    isKycLoading ||
    updateKycStatus === fetchStatuses.pending ||
    createStatus === fetchStatuses.pending ||
    updateStatus === fetchStatuses.pending;

  const activeTabValue = mapTabParamToValue[selectedTab] || DEFAULT_TAB_VALUE;

  const handleChange = (newValue: string) => {
    dispatch(resetUserApiStatuses());
    setSelectedTab(mapTabValueToTabParam[newValue]);
  };

  const isTabDisabled = React.useCallback(
    (tab: SideTabDescriptor) => isLoading || (tab.value === KYC_TAB_VALUE && !canReadKyc),
    [isLoading, canReadKyc],
  );

  useEffect(() => {
    dispatch(fetchUserStatusOptions());
    dispatch(fetchUserGroups({ limit: 1000, offset: 0 }));
    dispatch(fetchKycTypeOptions());
    dispatch(fetchKycStatusOptions());
    dispatch(fetchKycGenderOptions());

    return () => {
      dispatch(resetEditUser());
      dispatch(cleanAlpacas());
    };
  }, []);

  useEffect(() => {
    if (Number(id)) {
      dispatch(fetchUser(Number(id)));
      dispatch(fetchKycByUserId(Number(id)));
      dispatch(fetchUserWorldCheckScreen(Number(id)));
      dispatch(fetchUserCustodianData(Number(id)));
    }
  }, [id]);

  useEffect(() => {
    if (activeTabValue === KYC_TAB_VALUE && !canReadKyc) {
      setSelectedTab(mapTabValueToTabParam[DEFAULT_TAB_VALUE]);
    }
  }, [activeTabValue, canReadKyc]);

  const renderActivePanel = () => {
    const userId = Number(id);

    switch (activeTabValue) {
      case '1':
        return <UserTab id={userId} />;
      case '2':
        return <KycTab id={userId} />;
      case '3':
        return <BankInfoTab id={userId} />;
      case '16':
        return <SubscriptionTab id={userId} />;
      case '4':
        return <OwnedSharesTab userId={userId} />;
      case '5':
        return <FeesTab userId={userId} />;
      case '6':
        return <AttachmentsTab id={userId} />;
      case '7':
        return <UserSurveyResultsTab id={userId} />;
      case '8':
        return <UserSignAgreementsTab id={userId} />;
      case '9':
        return <UserHistoryTab id={userId} />;
      case '10':
        return <WorldCheckTab userId={userId} />;
      case '11':
        return <AlpacaTab id={userId} />;
      case '12':
        return <Ledger accountId={userId} fetchLedger={useFetchLedgerQuery} type="cash" />;
      case '13':
        return <Ledger accountId={userId} fetchLedger={useFetchLedgerQuery} type="units" />;
      case '14':
        return <TimelineTab userId={userId} />;
      case '15':
        return <UserNotesTab userId={userId} />;
      default:
        return null;
    }
  };

  if (!id) {
    return (
      <Container component="main" maxWidth="xl">
        <div>Loading...</div>
      </Container>
    );
  }

  return (
    <Container component="main" maxWidth="xl" sx={{ pb: '32px' }}>
      <UserPageHeader user={editUser} isLoading={isUserLoading} />

      <SideTabsLayout
        sections={userTabSections}
        ariaLabel="User sections"
        value={activeTabValue}
        onChange={handleChange}
        isTabDisabled={isTabDisabled}
      >
        {renderActivePanel()}
      </SideTabsLayout>
    </Container>
  );
}
